import { Controller } from "@hotwired/stimulus"
import { addonCostLine } from "../../utils/addon-cost-line.util"
import { pluralText } from "../../utils/plural-category.util"

// Keep-N stepper on the downgrade impact block (spec §3.2): the customer picks
// how many over-limit rows stay as paid extras; the rest are listed as "will be
// suspended", newest first — the order DowngradeService walks — and counted on
// the feature's badge. The value is clamped here for the display only;
// UpgradeService clamps it again.
//
// `suspendableCurrent` is what DowngradeService counts against the limit (for
// users: memberships, not pending invitations), so the list shows who would
// really be suspended; `max` is the keep floor, which does count invitations.
// Every change is announced as billing--downgrade-keep:change so the checkout's
// order summary (billing--downgrade-summary) re-prices the next period.
//
// The saved-card form's "use a different card" link reloads the checkout, so
// every change also writes the count into that link's keep[<feature>] param.
// The link sits outside this controller, in the form the keep input joins; it
// is found there by its data-downgrade-keep-link hook, never by position.
export default class extends Controller {
  static targets = ["input", "total", "candidate", "suspendedTitle", "badge", "decrementButton", "incrementButton"]
  static values = {
    featureKey: String,
    suspendableCurrent: Number,
    newLimit: Number,
    max: Number,
    unitPrice: Number,
    currencyFormat: { type: Object, default: {} },
    texts: { type: Object, default: {} },
    willSuspendTexts: { type: Object, default: {} }
  }

  connect() {
    this.update()
  }

  increment() {
    this.#write(this.#quantity() + 1)
  }

  decrement() {
    this.#write(this.#quantity() - 1)
  }

  update() {
    const quantity = this.#clamp(this.#quantity())
    if (String(quantity) !== this.inputTarget.value) this.inputTarget.value = quantity
    const suspended = this.#suspendedCount(quantity)
    this.#renderTotal(quantity)
    this.#renderCandidates(suspended)
    this.#renderBadge(suspended)
    if (this.hasDecrementButtonTarget) this.decrementButtonTarget.disabled = quantity <= 0
    if (this.hasIncrementButtonTarget) this.incrementButtonTarget.disabled = quantity >= this.maxValue
    this.#writeKeepLinks(quantity)
    this.dispatch("change", { detail: { featureKey: this.featureKeyValue, quantity } })
  }

  #write(quantity) {
    this.inputTarget.value = this.#clamp(quantity)
    this.update()
  }

  #quantity() {
    return Number.parseInt(this.inputTarget.value, 10) || 0
  }

  #clamp(quantity) {
    return Math.min(Math.max(quantity, 0), this.maxValue)
  }

  #suspendedCount(quantity) {
    const excess = Math.max(this.suspendableCurrentValue - this.newLimitValue - quantity, 0)
    return Math.min(excess, this.candidateTargets.length)
  }

  #renderTotal(quantity) {
    if (!this.hasTotalTarget) return
    this.totalTarget.textContent = addonCostLine({
      quantity,
      unitPrice: this.unitPriceValue,
      currencyFormat: this.currencyFormatValue,
      period: this.textsValue.period,
      pattern: this.textsValue.costLine,
      zero: this.textsValue.zero
    })
  }

  #renderCandidates(suspended) {
    this.candidateTargets.forEach((element, index) => {
      element.hidden = index >= suspended
    })
    if (!this.hasSuspendedTitleTarget) return
    this.suspendedTitleTarget.textContent =
      suspended === 0 ? this.textsValue.noneSuspended || "" : this.textsValue.suspendedTitle || ""
  }

  // The server clamps the param again; this only keeps the link honest.
  #writeKeepLinks(quantity) {
    const links = this.inputTarget.form?.querySelectorAll("a[data-downgrade-keep-link]") || []
    for (const link of links) {
      const url = new URL(link.href)
      url.searchParams.set(`keep[${this.featureKeyValue}]`, String(quantity))
      link.href = url.toString()
    }
  }

  #renderBadge(suspended) {
    if (!this.hasBadgeTarget) return
    this.badgeTarget.hidden = suspended === 0
    if (suspended === 0) return
    const text = pluralText(this.willSuspendTextsValue, document.documentElement.lang, suspended)
    this.badgeTarget.textContent = text.replace("%{count}", String(suspended))
  }
}
